import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DxPieChartModule} from 'devextreme-angular';
import {hasScoreData, NoData, noDataColor, noDataRing, ScoreFormatMode, scoreCenterLabel} from '../doughnut-chart/score-center';

export type DoughnutChartTableSlice = { argument: string; value: number };

@Component({
    selector: 'app-doughnut-chart-table',
    imports: [CommonModule, DxPieChartModule],
    templateUrl: './doughnut-chart-table.component.html',
})
export class DoughnutChartTableComponent implements OnChanges {
    @Input() title = '';
    @Input() slices: DoughnutChartTableSlice[] = [];
    @Input() palette: Record<string, string> = {};
    @Input() score: number | null | undefined = null;
    @Input() formatMode: ScoreFormatMode = 'percent';
    @Input() centerText = 'My Score';
    @Input() showPercent = false;
    @Input() valueHeader = 'Instances';

    private chart: any;
    protected hiddenArguments = new Set<string>();
    protected chartSlices: DoughnutChartTableSlice[] = [];
    protected hasData = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['slices']) {
            this.hasData = hasScoreData(this.slices);
            this.chartSlices = this.hasData || this.slices.length === 0 ? this.slices : noDataRing();
        }
    }

    // Consumers always send every slice once the score has loaded, so no slices means it is still loading.
    protected get centerLabel(): string {
        return this.slices.length ? scoreCenterLabel(this.hasData, this.score, this.formatMode) : '';
    }

    protected percent(slice: DoughnutChartTableSlice): number {
        const total = this.slices.reduce((sum, s) => sum + s.value, 0);
        return total === 0 ? 0 : Math.round((slice.value / total) * 10000) / 100;
    }

    protected color = (info: { argument: string }) => ({
        color: info.argument === NoData ? noDataColor : this.palette[info.argument] ?? 'rgba(100, 100, 100, 0.5)',
    });

    protected customizeTooltip = ({argument, value}: { argument: string; value: number }) => ({
        text: `${argument}: ${value}`,
    });

    onChartInitialized(e: { component?: any }): void {
        this.chart = e.component;
    }

    private getPoints(argument: string): any[] {
        const series = this.chart?.getAllSeries?.()[0];
        return series?.getPointsByArg?.(argument) ?? [];
    }

    protected onLegendClick(argument: string): void {
        const points = this.getPoints(argument);
        const willHide = points.some(p => p.isVisible());
        points.forEach(p => (willHide ? p.hide() : p.show()));
        if (willHide) {
            this.hiddenArguments.add(argument);
        } else {
            this.hiddenArguments.delete(argument);
        }
    }

    protected onLegendHover(argument: string, hovered: boolean): void {
        this.getPoints(argument).forEach(p => (hovered ? p.hover() : p.clearHover()));
    }
}
